import { PaintColorInfo } from "@omni/types/api/paintColors/bff/shared/paintColorInfo.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { Mapped } from "@omni/utils/context/mapped.context";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useMemo } from "react";

import { sortColorsByLuminance } from "@/components/PaintConfigurator/helpers/color.helper";

type ColorsContextValue = {
    colors: Array<PaintColorInfo>;
};

type ColorsContextProviderProps = RequireChildrenFor<{
    colors?: Array<PaintColorInfo>;
}>;

export const ColorsContext = createContext<ColorsContextValue | undefined>(
    undefined
);
ColorsContext.displayName = "ColorsContext";

export const ColorsContextProvider = ({
    colors = [],
    children
}: ColorsContextProviderProps) => {
    const sortedColors = useMemo(() => {
        if (!isDefined(colors)) return [];

        return sortColorsByLuminance(colors, (c) => c.hexValue);
    }, [colors]);

    return (
        <ColorsContext value={{ colors: sortedColors }}>
            <Mapped array={sortedColors}>{children}</Mapped>
        </ColorsContext>
    );
};

export const useColorsContext = () => {
    const context = use<ColorsContextValue | undefined>(ColorsContext);

    if (!isDefined(context)) {
        throw new Error(`${ColorsContext.displayName} is undefined.`);
    }

    return context;
};
